import { useState } from 'react';
import { useParams } from 'react-router';
import { DateTime } from 'luxon';
import { IPaymentPlan, OrganisationFeatureFlags } from 'shared';

import { PaymentPlanStrip } from '../../../../common/Components/PaymentPlan/PaymentPlanStrip';
import { useInvoiceDetails } from '../../../../common/hooks/useInvoiceDetails';
import { useAdhocActionModal } from '../../../hooks/useAdhocActionModal';
import { useGetSelectedOrganisation } from '../../../hooks/useGetSelectedOrganisation';
import { useInvoice } from '../../../hooks/useInvoice';
import { useFetchSavedCardsQuery } from '../../../services/api/invoiceApi/invoice';
import { FromCall } from '../../Molecules/CallTranscript';
import { RemovePaymentPlanModal } from '../InvoiceDocument/InvoicePaymentPlan';
import { PayWithSavedCardModal } from '../PayWithSavedCardModal';

export const PAYMENT_PLAN_STRIP_ID = `invoice-payment-plan`;

/** The invoice's active payment plan, if any. Plans are deleted rather than closed, so any returned plan is active */
export function useActivePaymentPlan(): IPaymentPlan | undefined {
  const { invoice } = useInvoice();

  return (invoice?.paymentPlans as IPaymentPlan[] | undefined)?.[0];
}

/** Summary of the active payment plan at the top of the invoice card */
export function InvoicePaymentPlanStrip() {
  const { organisationId, invoiceId } = useParams();
  const organisation = useGetSelectedOrganisation();
  const { invoice } = useInvoice();
  const invoiceDetails = useInvoiceDetails(invoice);
  const plan = useActivePaymentPlan();
  const { openSendInvoiceEmail } = useAdhocActionModal();
  const [removeOpen, setRemoveOpen] = useState(false);
  const [savedCardOpen, setSavedCardOpen] = useState(false);
  // Same query as the header button, so it shares the cached result
  const { data: savedCardQuote } = useFetchSavedCardsQuery({ organisationId, invoiceId }, {
    skip: !plan || !organisation?.featureFlags?.includes(OrganisationFeatureFlags.PORTAL_PAYMENT_METHODS) || invoice?.xero_status !== `AUTHORISED`,
  });

  if (!plan || !invoice) return null;

  const timezone = invoice.organisationTimezone;
  const createdBy = `Plan created by ${plan.createdByUserReference || `Unknown User`} on ${
    DateTime.fromJSDate(new Date(plan.createdAt)).setZone(timezone).toFormat(`d LLL yyyy, HH:mm`)}`;

  return (
    <>
      <PaymentPlanStrip
        id={ PAYMENT_PLAN_STRIP_ID }
        plan={ plan }
        currency={ invoice.xero_currency }
        timezone={ timezone }
        footnote={ createdBy }
        onRemovePlan={ () => setRemoveOpen(true) }
        // The charge always goes to the next unpaid instalment, since payments fill instalments oldest first
        payInstalment={ savedCardQuote?.paymentMethods.length && savedCardQuote.instalmentNumber ? {
          number: savedCardQuote.instalmentNumber,
          label: `Pay with saved card`,
          onClick: () => setSavedCardOpen(true),
        } : undefined }
        actions={ (
          <>
            <span className={ `text-xs text-gray-500` }>{ createdBy }</span>
            { plan.contactCallId ? (
              <FromCall
                organisationId={ plan.organisationId }
                contactId={ plan.contactId }
                contactCallId={ plan.contactCallId }
              />
            ) : null }
            <button
              type={ `button` }
              onClick={ () => setRemoveOpen(true) }
              className={ `cursor-pointer h-7 whitespace-nowrap rounded-md border border-red-200 bg-white px-2.5 text-xs font-medium text-paidniceRed-700 hover:bg-red-50` }
            >
              { `Remove plan` }
            </button>
          </>
        ) }
      />
      <RemovePaymentPlanModal
        open={ removeOpen }
        setOpen={ setRemoveOpen }
        plan={ plan }
      />
      { savedCardQuote && (
        <PayWithSavedCardModal
          open={ savedCardOpen }
          onClose={ () => setSavedCardOpen(false) }
          quote={ savedCardQuote }
          invoiceNumber={ invoiceDetails.number }
          onSendReminder={ () => openSendInvoiceEmail(invoiceId, invoiceDetails.number) }
        />
      ) }
    </>
  );
}
